import { useState } from 'react'
import './ProductImageDeck.css'
export type ProductPhotoDeckPhoto = { id: string; label: string; imageUrl: string | null }
function ProductPhoto({ entity }: { entity: ProductPhotoDeckPhoto }) {
  const [failed, setFailed] = useState(false)
  if (!entity.imageUrl || failed)
    return (
      <span className="product-image-deck__missing" role="img" aria-label={`No image for ${entity.label}`}>
        <s-icon type="image" />
      </span>
    )
  return (
    <img
      src={entity.imageUrl}
      alt={entity.label}
      width={64}
      height={64}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}
export const MAX_VISIBLE_PHOTOS = 3
/**
 * Reusable photo-only deck. Supply ordered photos with stable unique IDs and meaningful labels.
 * Shows up to three uncropped images, then a non-interactive +N marker. Missing/failed URLs
 * have labelled placeholders; fine-pointer hover lifts a frame and respects reduced motion, unless
 * `interactive={false}` (default true), which keeps every frame still.
 * Does not fetch products, infer relationships, or own navigation/actions.
 * @see docs/features/shopify/frontend/product-photo-deck.md
 */
export function ProductPhotoDeck({
  photos,
  interactive = true,
}: {
  photos: readonly ProductPhotoDeckPhoto[]
  interactive?: boolean
}) {
  const visible = photos.slice(0, MAX_VISIBLE_PHOTOS)
  let positions = ['left', 'right']
  if (visible.length === 1) positions = ['single']
  if (visible.length === 3) positions = ['left', 'centre', 'right']
  const remaining = photos.length - visible.length
  let className = 'product-image-deck'
  if (visible.length === 3) className += ' product-image-deck--triple'
  // A lone photo has no fan, so the deck is only as wide as its frame plus the hover-lift room.
  if (visible.length === 1 && remaining === 0) className += ' product-image-deck--single'
  if (!interactive) className += ' product-image-deck--static'
  let overflow = null
  if (remaining > 0)
    overflow = (
      <span className="product-image-deck__overflow" role="img" aria-label={`${remaining} more products`}>
        +{remaining}
      </span>
    )
  return (
    <div className={className}>
      {visible.map((entity, index) => (
        <span key={entity.id} className={`product-image-deck__card product-image-deck__card--${positions[index]}`}>
          <ProductPhoto key={entity.imageUrl} entity={entity} />
        </span>
      ))}
      {overflow}
    </div>
  )
}
/** The analytical pair surface deliberately supplies exactly two products. */
export function ProductImageDeck({ left, right }: { left: ProductPhotoDeckPhoto; right: ProductPhotoDeckPhoto }) {
  return <ProductPhotoDeck photos={[left, right]} />
}
